import { useState } from "react";
import { Button, Dialog } from "@omnabase/ui";
import type { Site } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useWs } from "../../lib/nav";
import { integrations, type Endpoint } from "../../lib/integrations";
import { lastAnswerLine, testFailedLine, testOutcomeLine } from "../../lib/sourceAnswers";
import { siteNameOf } from "../../lib/sites";
import { EndpointFieldsTable, FieldsHead } from "./EndpointSettings";

/** A connected site's Customer data dialog (Settings › Integrations › WordPress › a site's "Customer data fields…", `?wordpress=fields-<site
 *  id>`; M14 round three, moved out of `WordPressSettings.tsx` in the site test call round, 2026-11-04): the fields the site's source answers
 *  with, each with its two switches, as a company's source shows them.
 *
 *  The site test call round (brief `docs/briefs/site-test-call-round.md` and its decisions; it overturns the licence-and-fields round's Q-C7):
 *  "Send a test call" asks the site about the presser's own address through the saved source's test route, and the page reads the
 *  integrations again so the learned rows appear. One status line under the table: after a press, the outcome counted from this answer's
 *  `answered` ("Answered with 1 field." / "Answered with no customer data for <address>." / "Test call failed · …", the hourly limit's
 *  refusal too); before one, while the list is empty, the source's last answer (finding 6's order). Without the connections key the button
 *  is off (finding 9). The table sits right inside the dialog's markup (a guard of integrations round two looks for it there). */
export function SiteFieldsDialog({ site, endpoint, configured, reload, onClose }: { site: Site; endpoint: Endpoint | null; configured: boolean; reload: () => void; onClose: () => void }) {
  const host = siteNameOf(site);
  const test = useSiteTest(endpoint?.id ?? null, reload);
  const line = endpoint && (test.outcome ?? (endpoint.fields.length ? null : lastAnswerLine(endpoint, "site")));
  return (
    <Dialog width={640} title={`Customer data from ${host}`} intro="What the site tells OmnaBase about a customer; you choose what shows on the customer and what goes to the AI." onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      {endpoint ? <>
        <FieldsHead testing={test.testing} disabled={!configured} onTest={() => void test.run()} />
        {endpoint.fields.length > 0 && <EndpointFieldsTable endpoint={endpoint} reload={reload} />}
        {line && <p className="ob-dialog__hint" role="status">{line}</p>}
      </> : <p className="ob-dialog__hint">{host} answers no customer questions: another system answers for its project, or it was connected without customer data.</p>}
    </Dialog>
  );
}

/** the saved source's test call for the site's dialog: the outcome line from the answer (or the refusal), then the page reads again */
function useSiteTest(endpointId: string | null, reload: () => void) {
  const { slug } = useWs(); const { me } = useSession();
  const [testing, setTesting] = useState(false); const [outcome, setOutcome] = useState<string | null>(null);
  const run = async () => {
    if (!endpointId) return;
    setTesting(true);
    try { setOutcome(testOutcomeLine(await integrations.testEndpoint(slug, endpointId), me.user.email)); reload(); }
    catch (err) { setOutcome(testFailedLine((err as Error).message)); }
    finally { setTesting(false); }
  };
  return { testing, outcome, run };
}
